import { ref, computed } from 'vue'

export interface DeviceConfig {
  type: 'phone' | 'iphone' | 'iphone-pro' | 'android' | 'tablet' | 'laptop' | 'desktop' | 'ultrawide' | 'tv' | 'watch'
  name: string
  frameClass?: string
  category?: 'mobile' | 'tablet' | 'desktop' | 'wearable' | 'tv'
  aspectRatio?: string
  resolution?: string
}

export const useDeviceMockups = () => {
  // Default device configurations matching the hero carousel
  const defaultDevices: DeviceConfig[] = [
    {
      type: 'phone',
      name: 'Phone',
      frameClass: 'phone-frame',
      category: 'mobile',
      aspectRatio: '9:19.5',
      resolution: '1080x2340'
    },
    {
      type: 'iphone',
      name: 'iPhone 12',
      frameClass: 'iphone-frame',
      category: 'mobile',
      aspectRatio: '9:19.5',
      resolution: '1170x2532'
    },
    {
      type: 'iphone-pro',
      name: 'iPhone Pro',
      frameClass: 'iphone-pro-frame',
      category: 'mobile',
      aspectRatio: '9:19.5',
      resolution: '1179x2556'
    },
    {
      type: 'android',
      name: 'Android',
      frameClass: 'android-frame',
      category: 'mobile',
      aspectRatio: '9:20',
      resolution: '1080x2400'
    },
    {
      type: 'tablet',
      name: 'Tablet',
      frameClass: 'tablet-frame',
      category: 'tablet',
      aspectRatio: '4:3',
      resolution: '2048x2732'
    },
    {
      type: 'laptop',
      name: 'Laptop',
      frameClass: 'laptop-frame',
      category: 'desktop',
      aspectRatio: '16:10',
      resolution: '1920x1200'
    },
    {
      type: 'desktop',
      name: 'Desktop',
      frameClass: 'desktop-frame',
      category: 'desktop',
      aspectRatio: '16:9',
      resolution: '1920x1080'
    },
    {
      type: 'ultrawide',
      name: 'Ultrawide',
      frameClass: 'ultrawide-frame',
      category: 'desktop',
      aspectRatio: '21:9',
      resolution: '3440x1440'
    },
    {
      type: 'tv',
      name: 'Smart TV',
      frameClass: 'tv-frame',
      category: 'tv',
      aspectRatio: '16:9',
      resolution: '3840x2160'
    },
    {
      type: 'watch',
      name: 'Smartwatch',
      frameClass: 'watch-frame',
      category: 'wearable',
      aspectRatio: '1:1',
      resolution: '396x396'
    }
  ]

  const devices = ref<DeviceConfig[]>(defaultDevices)
  const selectedDeviceIndex = ref(0)

  // Computed properties
  const selectedDevice = computed(() => devices.value[selectedDeviceIndex.value])
  
  const mobileDevices = computed(() => 
    devices.value.filter(device => device.category === 'mobile')
  )
  
  const tabletDevices = computed(() => 
    devices.value.filter(device => device.category === 'tablet')
  )
  
  const desktopDevices = computed(() => 
    devices.value.filter(device => device.category === 'desktop')
  )
  
  const wearableDevices = computed(() => 
    devices.value.filter(device => device.category === 'wearable')
  )
  
  const tvDevices = computed(() => 
    devices.value.filter(device => device.category === 'tv')
  )

  // Methods
  const getDeviceByType = (type: DeviceConfig['type']) => {
    return devices.value.find(device => device.type === type)
  }

  const getDevicesByCategory = (category: DeviceConfig['category']) => {
    return devices.value.filter(device => device.category === category)
  }

  const addDevice = (device: DeviceConfig) => {
    devices.value.push(device)
  }

  const removeDevice = (type: DeviceConfig['type']) => {
    const index = devices.value.findIndex(device => device.type === type)
    if (index > -1) {
      devices.value.splice(index, 1)
    }
  }

  const updateDevice = (type: DeviceConfig['type'], updates: Partial<DeviceConfig>) => {
    const index = devices.value.findIndex(device => device.type === type)
    if (index > -1) {
      devices.value[index] = { ...devices.value[index], ...updates }
    }
  }

  const selectDevice = (index: number) => {
    if (index >= 0 && index < devices.value.length) {
      selectedDeviceIndex.value = index
    }
  }

  const selectDeviceByType = (type: DeviceConfig['type']) => {
    const index = devices.value.findIndex(device => device.type === type)
    if (index > -1) {
      selectedDeviceIndex.value = index
    }
  }

  const resetToDefaults = () => {
    devices.value = [...defaultDevices]
    selectedDeviceIndex.value = 0
  }

  // Preset configurations for different use cases
  const getHeroCarouselDevices = () => defaultDevices

  const getMobileOnlyDevices = () => [
    defaultDevices[0], // phone
    defaultDevices[1], // iphone
    defaultDevices[2], // iphone-pro
    defaultDevices[3]  // android
  ]

  const getDesktopOnlyDevices = () => [
    defaultDevices[5], // laptop
    defaultDevices[6], // desktop
    defaultDevices[7]  // ultrawide
  ]

  const getPopularDevices = () => [
    defaultDevices[1], // iphone
    defaultDevices[3], // android
    defaultDevices[4], // tablet
    defaultDevices[6]  // desktop
  ]

  return {
    // State
    devices,
    selectedDeviceIndex,
    
    // Computed
    selectedDevice,
    mobileDevices,
    tabletDevices,
    desktopDevices,
    wearableDevices,
    tvDevices,
    
    // Methods
    getDeviceByType,
    getDevicesByCategory,
    addDevice,
    removeDevice,
    updateDevice,
    selectDevice,
    selectDeviceByType,
    resetToDefaults,
    
    // Presets
    getHeroCarouselDevices,
    getMobileOnlyDevices,
    getDesktopOnlyDevices,
    getPopularDevices
  }
}

// DeviceConfig interface is already exported above, no need to re-export